import React, { useState } from "react";
import ModalModern from "./ModalModern";
import ModernPopupBody from "./common/ModernPopupBody";
import CustomBrandedHeader from "./common/CustomBrandedHeader";
import LoadingPlaceholder from "../loading/LoadingPlaceholder";
import VerificationCodeInput from "../forms/VerificationCodeInput";
import TOTPSetupModal from "./TOTPSetupModal";

/**
 * VerificationModal - Modal for TOTP verification and mandatory MFA enrollment
 * @param {boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {Function} onConfirm - Callback when code is confirmed (receives code as parameter)
 * @param {Function} onSuccess - Callback when authentication successfully finishes
 * @param {string} email - User's email address
 * @param {string} step - Current step: "loading", "verification", "totp-setup", "error", or "success"
 * @param {string|null} mfaSetupToken - Temporary Bearer token for first-time TOTP setup
 * @param {Function} onSetupComplete - Callback executed after TOTP setup activation succeeds
 * @param {boolean} isLoading - Whether verification network request is in progress
 * @param {Array} errors - Array of error messages to display
 * @param {Function} onDismissError - Callback to dismiss error display
 * @param {string} verificationTitle - Custom title for verification step
 * @param {string|null} verificationMessage - Custom message for verification step
 * @param {string} confirmButtonText - Custom text for the confirm button
 * @param {string|null} successMessage - Custom message to display on success step
 * @param {string|null} verificationType = "Type of MFA"
 */
function VerificationModal({
  isOpen,
  onClose,
  onConfirm,
  onSuccess,
  email = "",
  step = "loading",
  verificationType = "email",
  mfaSetupToken = null,
  onSetupComplete = () => {},
  isLoading = false,
  errors = [],
  onDismissError = () => {},
  verificationTitle = null,
  verificationMessage = null,
  confirmButtonText = "Confirm",
  successMessage = null,
}) {
  const [verificationCode, setVerificationCode] = useState("");

  if (!isOpen) return null;

  const isTotp = verificationType === "totp";

  const defaultVerificationTitle = isTotp
    ? "Two-Factor Authentication"
    : "Verify Email";

  const title = verificationTitle || defaultVerificationTitle;

  const handleConfirm = () => {
    if (verificationCode.length === 6 && onConfirm) {
      onConfirm(verificationCode);
    }
  };

  const handleClose = () => {
    setVerificationCode("");

    if (onClose) {
      onClose();
    }
  };

  /**
   * Render the verification instructions depending on the type.
   */
  const renderVerificationMessage = () => {
    if (verificationMessage) {
      // Existing email behavior:
      // Highlight the user's email inside a custom message.
      if (!isTotp && email) {
        return (
          <p style={{ marginTop: "10px" }}>
            {verificationMessage.split(email).map((part, index, array) => (
              <React.Fragment key={index}>
                {part}

                {index < array.length - 1 && <strong>{email}</strong>}
              </React.Fragment>
            ))}
          </p>
        );
      }

      // TOTP or custom message without email.
      return <p style={{ marginTop: "10px" }}>{verificationMessage}</p>;
    }

    // TOTP default message
    if (isTotp) {
      return (
        <p style={{ marginTop: "10px" }}>
          Enter the 6-digit code from your authenticator app (Google
          Authenticator, Authy, or 1Password).
        </p>
      );
    }

    // Existing email default message
    return (
      <p style={{ marginTop: "10px" }}>
        We've sent a 6-digit code to <strong>{email}</strong>. Please enter it
        below.
      </p>
    );
  };

  return (
    <ModalModern onClose={handleClose}>
      <CustomBrandedHeader onForceLogout={handleClose} />

      <ModernPopupBody>
        <div
          className="session-body-wrapper"
          style={{
            paddingTop: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            maxWidth: "400px",
            margin: "0 auto",
          }}
        >
          {/* LOADING */}
          {step === "loading" && (
            <div
              style={{
                width: "100%",
                textAlign: "center",
              }}
            >
              <LoadingPlaceholder rows={1} type="loading-card" />
            </div>
          )}

          {/* FIRST-TIME TOTP SETUP */}
          {step === "totp-setup" && (
            <TOTPSetupModal
              mfaSetupToken={mfaSetupToken}
              onSetupComplete={onSetupComplete}
            />
          )}

          {/* EMAIL / TOTP VERIFICATION */}
          {step === "verification" && (
            <div style={{ width: "100%" }}>
              <p className="auth-form-title" style={{ textAlign: "center" }}>
                {title}
              </p>

              {isLoading ? (
                <LoadingPlaceholder rows={1} type="loading-card" />
              ) : errors.length > 0 ? (
                <div className="verification-text-center">
                  <p className="verification-error-message">{errors[0]}</p>

                  <div className="verification-actions">
                    <button
                      type="button"
                      className="cta-button secondary"
                      onClick={handleClose}
                    >
                      Close
                    </button>

                    <button
                      type="button"
                      className="cta-button primary"
                      onClick={onDismissError}
                    >
                      Re-try
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    className={`verification-message${
                      verificationMessage ? " left" : ""
                    }`}
                  >
                    {renderVerificationMessage()}
                  </div>

                  <VerificationCodeInput
                    value={verificationCode}
                    onChange={setVerificationCode}
                    disabled={isLoading}
                  />

                  <div className="verification-actions margin-top">
                    <button
                      type="button"
                      className="cta-button primary"
                      onClick={handleConfirm}
                      disabled={verificationCode.length !== 6 || isLoading}
                    >
                      {confirmButtonText}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ERROR */}
          {step === "error" && (
            <div className="verification-text-center">
              <p className="auth-form-title">Verification Failed</p>

              <p className="verification-message">
                {errors.length > 0 ? errors[0] : "An error occurred."}
              </p>

              <div className="verification-actions">
                <button
                  type="button"
                  className="cta-button primary"
                  onClick={handleClose}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {/* SUCCESS */}
          {step === "success" && (
            <div className="verification-text-center">
              <div
                className="verification-success-icon"
                style={{ marginBottom: "15px" }}
              >
                ✓
              </div>

              <p className="auth-form-title">Success!</p>

              <p className="verification-success-message">
                {successMessage ||
                  (title.includes("Account")
                    ? "Your account has been created."
                    : "Verification successful.")}
              </p>

              <div className="verification-actions">
                <button
                  type="button"
                  className="cta-button primary"
                  onClick={() => {
                    if (onSuccess) {
                      onSuccess();
                    }

                    handleClose();
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          )}
        </div>
      </ModernPopupBody>
    </ModalModern>
  );
}

export default VerificationModal;
